import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Clock, ArrowRight, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSubscription } from '@/hooks/useSubscription'
import { useState } from 'react'

interface SubscriptionBannerProps {
  churchId: string | null | undefined
}

export default function SubscriptionBanner({ churchId }: SubscriptionBannerProps) {
  const navigate = useNavigate()
  const { status, trialDaysRemaining, limits, loading } = useSubscription(churchId)
  const [dismissed, setDismissed] = useState(false)

  if (loading || dismissed) return null

  const subscriptionStatus = status?.status
  const daysUntilExpiry = status?.days_until_expiry

  // Active subscription with no issues - hide banner
  if (subscriptionStatus === 'active' && (daysUntilExpiry === null || daysUntilExpiry === undefined || daysUntilExpiry > 14)) {
    return null
  }

  let bgColor = ''
  let textColor = ''
  let message = ''
  let ctaText = ''
  let ctaPath = ''
  let icon = <Clock className="h-4 w-4" />
  let dismissable = true

  if (subscriptionStatus === 'trial') {
    if (trialDaysRemaining <= 0) {
      // Trial expired
      bgColor = 'bg-red-50 dark:bg-red-950/50 border-red-200 dark:border-red-800'
      textColor = 'text-red-800 dark:text-red-200'
      message = 'Your free trial has expired. Upgrade now to continue using FaithYoke.'
      ctaText = 'Upgrade Now'
      ctaPath = '/pricing'
      icon = <AlertTriangle className="h-4 w-4" />
      dismissable = false
    } else {
      // Trial active
      bgColor = 'bg-blue-50 dark:bg-blue-950/50 border-blue-200 dark:border-blue-800'
      textColor = 'text-blue-800 dark:text-blue-200'
      message = `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left in your free trial. Upgrade to keep all features.`
      ctaText = 'View Plans'
      ctaPath = '/pricing'
      icon = <Clock className="h-4 w-4" />
    }
  } else if (subscriptionStatus === 'expired' || subscriptionStatus === 'past_due') {
    bgColor = 'bg-red-50 dark:bg-red-950/50 border-red-200 dark:border-red-800'
    textColor = 'text-red-800 dark:text-red-200'
    message = 'Your subscription has expired. Renew to continue accessing all features.'
    ctaText = 'Renew Now'
    ctaPath = `/checkout?plan=${limits?.plan || 'growth'}&billing=annual`
    icon = <AlertTriangle className="h-4 w-4" />
    dismissable = false
  } else if (subscriptionStatus === 'cancelled') {
    bgColor = 'bg-amber-50 dark:bg-amber-950/50 border-amber-200 dark:border-amber-800'
    textColor = 'text-amber-800 dark:text-amber-200'
    message = 'Your subscription has been cancelled. Reactivate to keep your data accessible.'
    ctaText = 'Reactivate'
    ctaPath = '/pricing'
    icon = <AlertTriangle className="h-4 w-4" />
    dismissable = false
  } else if (subscriptionStatus === 'active' && daysUntilExpiry != null && daysUntilExpiry <= 14) {
    bgColor = 'bg-amber-50 dark:bg-amber-950/50 border-amber-200 dark:border-amber-800'
    textColor = 'text-amber-800 dark:text-amber-200'
    message = `Your subscription expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}. Renew now to avoid interruption.`
    ctaText = 'Renew'
    ctaPath = `/checkout?plan=${limits?.plan || 'growth'}&billing=annual`
    icon = <Clock className="h-4 w-4" />
  } else {
    return null
  }

  return (
    <div className={`${bgColor} border-b px-4 py-3`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className={`flex items-center gap-2 ${textColor}`}>
          {icon}
          <span className="text-sm font-medium">{message}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className={textColor}
            onClick={() => navigate(ctaPath)}
          >
            {ctaText}
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
          {dismissable && (
            <button
              onClick={() => setDismissed(true)}
              className={`${textColor} hover:opacity-70`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
